import { Fragment, useContext, useState, useEffect, type PropsWithChildren } from "react";
import { EditableGroupContext } from "../../../components/editable/EditableGroup/EditableGroup";
import { AssetLocation, AssetLocationTypes, Booking, BookingAltId, KeyManagementTypes, LaneAllocationSystem, User, api } from "../../../logic/api";
import LocationPickerDialog from "../../../components/pickers/locationPicker/LocationPickerDialog";
import { Box, Button, Divider, GridLegacy as Grid } from "@mui/material";
import flex from "../../../styles/flex";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { SessionContext } from "../../../utility/useSession";
import { useTranslation } from "react-i18next";
import VehicleAllocationPickerDialog from "./VehicleAllocationPickerDialog";
import { usePreferences } from "../../../utility/usePreferences";
import { validateTrackingIdFormat } from "@/utility/validation";
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";

interface IBookingMovementProps {
  booking: Booking,
}

export enum Location {
  VEHICLE = 1,
  KEY = 2
}

const BookingMovement = (props: PropsWithChildren<IBookingMovementProps>) => {
  const state = useContext(EditableGroupContext);
  const session = useContext(SessionContext);
  const { kmosVersion, kmosManualAllocationEnabled, useTrackingId, trackingIdRegex } = usePreferences();
  const { t } = useTranslation()
  const [showAllocateVehicle, setShowAllocateVehicle] = useState(false);
  const [showMoveVehicle, setShowMoveVehicle] = useState(false);
  const [showMoveKeys, setShowMoveKeys] = useState(false);
  const [showMovedBy, setShowMovedBy] = useState(false);
  const [locationBeingChanged, setLocationBeingChanged] = useState<Location | null>(null);
  // Pending location changes (new workflow)
  const [pendingVehicleLocation, setPendingVehicleLocation] = useState<AssetLocation | null>(null);
  const [pendingKeyLocation, setPendingKeyLocation] = useState<AssetLocation | null>(null);

  // Local state (old workflow, delete with isVehicleMovementAssumeKeyholder)
  const [localAllocatedLocation, setLocalAllocatedLocation] = useState<AssetLocation | null>(null);
  const [localVehicleLocation, setLocalVehicleLocation] = useState<AssetLocation | null>(null);
  const [localKeyLocation, setLocalKeyLocation] = useState<AssetLocation | null>(null);

  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);
  const isSydneyDualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isVehicleMovementAssumeKeyholderReleased = useReleaseManagement(ReleaseFlag.releaseVehicleMovementAssumeKeyholder);
  const isMovementCorrectionReleased = useReleaseManagement(ReleaseFlag.releaseMarkVehicleMovementAsCorrection);

  const releaseAdditionalExternalReferences = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
  const isTrackingIdValidationReleased = useReleaseManagement(ReleaseFlag.releaseTrackingIdValidation);

  const getUsersByStaffPass = api.hooks.users.getUserByStaffPass();

  const booking = state.data as Booking;

  const isDualParkSystemEnabled =
    isSydneyDualParkAllocationReleased &&
    session.current.site?.preferences.lane_allocation_system === LaneAllocationSystem.DualPark;

  useEffect(() => {
    const { allocatedLane, allocatedLaneId, allocatedRow } = props.booking?.vehicle;

    if (isDualParkSystemEnabled) {
      if (allocatedLane !== undefined) {
        booking.vehicle.allocatedLane = allocatedLane;
      }
      if (allocatedLaneId !== undefined) {
        booking.vehicle.allocatedLaneId = allocatedLaneId;
      }
      if (allocatedRow !== undefined) {
        booking.vehicle.allocatedRow = allocatedRow;
      }
    }
  }, [isDualParkSystemEnabled, props.booking?.vehicle.allocatedLane, props.booking?.vehicle.allocatedLaneId, props.booking?.vehicle.allocatedRow]);

  useEffect(() => {
    if (isVehicleMovementAssumeKeyholderReleased && !state.isEditing) {
      setLocalAllocatedLocation(null);
      setLocalVehicleLocation(null);
      setLocalKeyLocation(null);
      setPendingVehicleLocation(null);
      setPendingKeyLocation(null);
    }
  }, [state.isEditing]);

  const cloneBooking = () => {
    const cloned: Booking = JSON.parse(JSON.stringify(state.data));
    return cloned;
  }

  const allocatedLocation = isVehicleMovementAssumeKeyholderReleased
    ? (booking?.vehicle?.allocatedLane?.updated ? booking.vehicle.allocatedLane : null)
    : localAllocatedLocation;

  const vehicleLocation = isVehicleMovementAssumeKeyholderReleased
    ? (booking?.vehicle?.location?.updated ? booking.vehicle.location : null)
    : localVehicleLocation;

  const keyLocation = isVehicleMovementAssumeKeyholderReleased
    ? (booking?.vehicleKey?.location?.updated ? booking.vehicleKey.location : null)
    : localKeyLocation;

  if (state.isEditing == false) {
    return <Fragment>
      <Grid container>
        {props.children}
      </Grid>
    </Fragment>
  }

  const applyVehicleLocationChange = (location: AssetLocation, movedBy?: User) => {
    if (state?.setData) {
      const data = cloneBooking();
      data.vehicle.location = location;
      data.vehicle.location.updated = true;
      if (movedBy) {
        data.vehicle.movedBy = movedBy;
      }
      state.setData(data);
    }
  }

  const applyKeyLocationChange = (location: AssetLocation, movedBy?: User) => {
    if (state?.setData) {
      const data = cloneBooking();
      data.vehicleKey.location = location;
      data.vehicleKey.location.updated = true;
      if (movedBy) {
        data.vehicleKey.movedBy = movedBy;
      }
      state.setData(data);
    }
  }

  const applyAllocatedLocationChange = (location: AssetLocation) => {
    if (state?.setData) {
      const data = cloneBooking();
      data.vehicle.allocatedLane = location;
      data.vehicle.allocatedLane.updated = true;
      state.setData(data);
    }
  }

  const clearVehicleLocation = () => {
    if (isVehicleMovementAssumeKeyholderReleased) {
      if (state?.setData) {
        const data = cloneBooking();
        if (data.vehicle.location) {
          data.vehicle.location.updated = false;
        }
        state.setData(data);
      }
      setPendingVehicleLocation(null);
    } else {
      setLocalVehicleLocation(null);
    }
  }

  const clearKeyLocation = () => {
    if (isVehicleMovementAssumeKeyholderReleased) {
      if (state?.setData) {
        const data = cloneBooking();
        if (data.vehicleKey.location) {
          data.vehicleKey.location.updated = false;
        }
        state.setData(data);
      }
      setPendingKeyLocation(null);
    } else {
      setLocalKeyLocation(null);
    }
  }

  const clearAllocatedLocation = () => {
    if (isVehicleMovementAssumeKeyholderReleased) {
      if (state?.setData) {
        const data = cloneBooking();
        if (data.vehicle.allocatedLane) {
          data.vehicle.allocatedLane.updated = false;
        }
        state.setData(data);
      }
    } else {
      setLocalAllocatedLocation(null);
    }
  }

  const onCancel = () => {
    setShowMovedBy(false);
    setLocationBeingChanged(null)
    setShowMoveVehicle(false);
    setShowMoveKeys(false);
    setShowAllocateVehicle(false);

    if (isVehicleMovementAssumeKeyholderReleased) {
      setPendingVehicleLocation(null);
      setPendingKeyLocation(null);
    } else {
      setLocalVehicleLocation(null);
      setLocalKeyLocation(null);
      setLocalAllocatedLocation(null);
    }
  }

  const keySelectionDisplayTypes = [
    AssetLocationTypes.KeyBox,
    AssetLocationTypes.DynamicBox,
    AssetLocationTypes.Hook,
    AssetLocationTypes.Keyroom,
    AssetLocationTypes.Lane,
    AssetLocationTypes.User,
    AssetLocationTypes.Users,
    AssetLocationTypes.Zone,
  ];

  const keySelectionTypes = [
    AssetLocationTypes.Hook,
    AssetLocationTypes.KeyBox,
    AssetLocationTypes.DynamicBox,
    AssetLocationTypes.Keyroom,
    AssetLocationTypes.Lane,
    AssetLocationTypes.User,
    AssetLocationTypes.Zone,
  ];

  const keyManagementOverrideTypes = [
    AssetLocationTypes.KeyBox,
    AssetLocationTypes.DynamicBox,
    AssetLocationTypes.Keyroom,
    AssetLocationTypes.User,
  ];

  const movedByDisplayTypes = [
    AssetLocationTypes.User,
    AssetLocationTypes.Users,
  ]

  const movedByTypes = [
    AssetLocationTypes.User,
  ]

  const getFullName = (user: User): string => {
    return `${user.firstName} ${user.lastName}`;
  }

  const kmosV2 = kmosVersion === "2.0";
  const isKmosManualAllocationEnabled =
    kmosManualAllocationReleased &&
    kmosV2 === true &&
    kmosManualAllocationEnabled === true;
  const showManualLaneAllocation = isKmosManualAllocationEnabled || isDualParkSystemEnabled;

  return (
    <Fragment>
      <Grid container>
        <div style={{ padding: 20, textAlign: "center", width: "100%" }}>
          <div style={{ textAlign: "left", display: "inline-block" }}>
            {showManualLaneAllocation && (
              <div style={{ paddingBottom: 20 }}>
                <div style={{ paddingBottom: 10 }}>
                  <b>{t("booking.labels.allocatedLane")}</b>
                </div>

                <div style={flex.flexRows}>
                  <div style={flex.flexAuto}>
                    {
                      allocatedLocation == null && (
                        <Button data-testid="booking-location-allocated-lane-select-button" variant="contained" color="secondary" onClick={() => {
                          setShowAllocateVehicle(true);
                        }}>
                          {t("general.select")}
                        </Button>
                      )
                    }
                    {
                      allocatedLocation != null && (
                        <Button
                          data-testid="booking-location-allocated-lane-clear-button"
                          variant="contained"
                          onClick={() => {
                            clearAllocatedLocation();
                          }}>
                          {t("general.clear")}
                        </Button>
                      )
                    }
                  </div>
                  <div style={flex.flexFill}>
                    <div style={{ padding: 8 }}>
                      {(allocatedLocation != null) ? (
                        <div>
                          {allocatedLocation.parentName} / <b>{allocatedLocation.name}</b>
                        </div>
                      ) : (
                        <div>
                          {t("input.validations.noLocationSelected")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

            )}

            <div style={{ paddingBottom: 10, }}>
              <b>{t("booking.labels.vehicleLocation")}</b>
            </div>
            <div style={flex.flexRows}>


              <div style={flex.flexAuto}>
                {
                  vehicleLocation == null && (
                    <Button data-testid="booking-location-vehicle-select-button" variant="contained" color="secondary" onClick={() => {
                      setShowMoveVehicle(true);
                    }}>
                      {t("general.select")}
                    </Button>
                  )
                }
                {
                  vehicleLocation != null && (
                    <Button
                      data-testid="booking-location-vehicle-clear-button"
                      variant="contained"
                      onClick={() => {
                        clearVehicleLocation();
                      }}>
                      {t("general.clear")}
                    </Button>
                  )
                }
              </div>
              <div style={flex.flexFill}>
                <div style={{ padding: 8 }}>
                  {(vehicleLocation != null) ? (
                    <div>
                      {vehicleLocation.parentName} / <b>{vehicleLocation.name}</b>{booking.vehicle.movedBy ? ` / ${getFullName(booking.vehicle.movedBy)}` : ''}
                    </div>
                  ) : (
                    <div>
                      {t("input.validations.noLocationSelected")}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ paddingTop: 20, paddingBottom: 10 }}>
              <b>{t("booking.labels.keyLocation")}</b>
            </div>

            <div style={flex.flexRows}>
              <div style={flex.flexAuto}>

                {
                  keyLocation == null && (
                    <Button data-testid="booking-location-key-select-button" variant="contained" color="secondary" onClick={() => {
                      setShowMoveKeys(true);
                    }}>
                      {t("general.select")}
                    </Button>
                  )
                }
                {
                  keyLocation != null && (
                    <Button
                      data-testid="booking-location-key-clear-button"
                      variant="contained"
                      onClick={() => {
                        clearKeyLocation();
                      }}>
                      {t("general.clear")}
                    </Button>
                  )
                }
              </div>
              <div style={flex.flexFill}>
                <div style={{ padding: 8 }}>
                  {(keyLocation != null) ? (
                    <div>
                      {keyLocation.parentName} / <b>{keyLocation.name}</b>{booking.vehicleKey.movedBy ? ` / ${getFullName(booking.vehicleKey.movedBy)}` : ''}
                    </div>
                  ) : (
                    <div>
                      {t("input.validations.noLocationSelected")}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {releaseAdditionalExternalReferences && useTrackingId && (
              <>
                <Divider sx={{ ...flex.flexFill, mt: 2.5, pb: 0, borderColor: "#eeeeee" }} />
                <Box sx={flex.flexFill} onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}>
                  <EditableField<Booking, string>
                    readOnly={false}
                    inputType={EditableFieldTypes.text}
                    columnWidth={12}
                    maxLength={255}
                    label={t("booking.labels.trackingId")}
                    testId="booking-location-trackingId"
                    onProvide={(data) => {
                      return data.externalRefs?.length ? data.externalRefs[0].ref : "";
                    }}
                    onValidate={(_data, value) => {
                      if (value && isTrackingIdValidationReleased && !validateTrackingIdFormat(value as string, trackingIdRegex)) {
                        return t("settings.invalidTrackingId");
                      }
                      return null;
                    }}
                    onChange={(data, value) => {
                      const updatedData = { ...data };
                      if (!updatedData.externalRefs) {
                        updatedData.externalRefs = [];
                      }

                      updatedData.externalRefs[0] = { ref: value || "" } as BookingAltId;

                      if (state?.setData) {
                        state.setData(updatedData);
                      }
                    }}
                  />
                </Box>
              </>
            )}

          </div>

          <LocationPickerDialog
            title={t("booking.labels.moveVehicle")}
            show={showMoveVehicle}
            displayTypes={[AssetLocationTypes.Zone, AssetLocationTypes.Lane]}
            selectionTypes={[AssetLocationTypes.Lane]}
            onChange={(location) => {
              if (location != null) {
                if (isVehicleMovementAssumeKeyholderReleased) {
                  // Store location in pending state, wait for "moved by" selection
                  setPendingVehicleLocation(location);
                  setShowMovedBy(true);
                  setLocationBeingChanged(Location.VEHICLE);
                } else {
                  if (state?.setData) {
                    const data = cloneBooking();
                    data.vehicle.location = location;
                    data.vehicle.location.updated = true;
                    state.setData(data);
                    setLocalVehicleLocation(location);

                    setShowMovedBy(true);
                    setLocationBeingChanged(Location.VEHICLE);
                  }
                }
              }
              setShowMoveVehicle(false);
            }}
            onCancel={onCancel}
          />
          <LocationPickerDialog
            title={t("booking.labels.moveKeys")}
            show={showMoveKeys}
            displayTypes={keySelectionDisplayTypes}
            selectionTypes={keySelectionTypes}
            keyManagementTypes={[KeyManagementTypes.box, KeyManagementTypes.hook]}
            keyManagementOverrideTypes={keyManagementOverrideTypes}
            onChange={(location) => {
              if (location != null) {
                if (isVehicleMovementAssumeKeyholderReleased) {
                  // Store location in pending state, wait for "moved by" selection
                  setPendingKeyLocation(location);
                  setShowMovedBy(true);
                  setLocationBeingChanged(Location.KEY);
                } else {
                  if (state?.setData) {
                    const data = cloneBooking();
                    data.vehicleKey.location = location;
                    data.vehicleKey.location.updated = true;
                    state.setData(data);
                    setLocalKeyLocation(location);

                    setShowMovedBy(true);
                    setLocationBeingChanged(Location.KEY);
                  }
                }
              }
              setShowMoveKeys(false);
            }}
            onCancel={onCancel}
          />
          <LocationPickerDialog
            title={t("booking.labels.movedBy")}
            subtitle={locationBeingChanged === Location.VEHICLE ? t("booking.labels.movedBySubtitle") : undefined}
            show={showMovedBy}
            displayTypes={movedByDisplayTypes}
            selectionTypes={movedByTypes}
            onChange={(user) => {
              if (user != null) {
                getUsersByStaffPass.send(session.current.context, { staffPass: user.code }, (userResponse) => {
                  if (!userResponse) {
                    throw new Error(t("errors.userDoesNotExist"));
                  }

                  if (isVehicleMovementAssumeKeyholderReleased) {
                    // Apply the pending location change with the moved-by user
                    if (locationBeingChanged === Location.VEHICLE && pendingVehicleLocation) {
                      applyVehicleLocationChange(pendingVehicleLocation, userResponse as User);
                    } else if (locationBeingChanged === Location.KEY && pendingKeyLocation) {
                      applyKeyLocationChange(pendingKeyLocation, userResponse as User);
                    }
                  } else {
                    // Just update the movedBy field
                    const data = cloneBooking();
                    if (locationBeingChanged === Location.VEHICLE) {
                      data.vehicle.movedBy = userResponse as User;
                    } else if (locationBeingChanged === Location.KEY) {
                      data.vehicleKey.movedBy = userResponse as User;
                    }
                    if (state?.setData) {
                      state.setData(data);
                    }
                  }
                })
              }

              setShowMovedBy(false);
              setLocationBeingChanged(null);

              if (isVehicleMovementAssumeKeyholderReleased) {
                setPendingVehicleLocation(null);
                setPendingKeyLocation(null);
              }
            }}
            onUnknownMovement={() => {
              const unknownUser = {
                id: -1,
                firstName: t("booking.labels.unknownUser"),
                lastName: "",
              } as User;

              if (isVehicleMovementAssumeKeyholderReleased) {
                // Apply the pending location change with unknown user
                if (locationBeingChanged === Location.VEHICLE && pendingVehicleLocation) {
                  applyVehicleLocationChange(pendingVehicleLocation, unknownUser);
                } else if (locationBeingChanged === Location.KEY && pendingKeyLocation) {
                  applyKeyLocationChange(pendingKeyLocation, unknownUser);
                }
              } else {
                // Just update the movedBy field
                const data = cloneBooking();
                if (locationBeingChanged === Location.VEHICLE) {
                  data.vehicle.movedBy = unknownUser;
                } else if (locationBeingChanged === Location.KEY) {
                  data.vehicleKey.movedBy = unknownUser;
                }
                if (state?.setData) {
                  state.setData(data);
                }
              }

              setShowMovedBy(false);
              setLocationBeingChanged(null);

              if (isVehicleMovementAssumeKeyholderReleased) {
                setPendingVehicleLocation(null);
                setPendingKeyLocation(null);
              }
            }}
            onCancel={onCancel}
            defaultExpanded={[AssetLocationTypes.Users]}
            onCorrection={() => {
              if (!isMovementCorrectionReleased) {
                return;
              }

              const correctionUser = {
                id: -1,
                firstName: t("booking.labels.correction"),
                lastName: "",
              } as User;

              if (locationBeingChanged === Location.VEHICLE && pendingVehicleLocation) {
                applyVehicleLocationChange(pendingVehicleLocation, correctionUser);
              } else if (locationBeingChanged === Location.KEY && pendingKeyLocation) {
                applyKeyLocationChange(pendingKeyLocation, correctionUser);
              }

              setShowMovedBy(false);
              setLocationBeingChanged(null);
              setPendingVehicleLocation(null);
              setPendingKeyLocation(null);
            }}
            locationType={locationBeingChanged ?? undefined}
          />

          {showManualLaneAllocation && (
            <VehicleAllocationPickerDialog
              show={showAllocateVehicle}
              onChange={(location) => {
                if (location != null) {
                  if (isVehicleMovementAssumeKeyholderReleased) {
                    applyAllocatedLocationChange(location);
                  } else {
                    if (state?.setData) {
                      const data = cloneBooking();
                      data.vehicle.allocatedLane = location;
                      data.vehicle.allocatedLane.updated = true;
                      state.setData(data);
                      setLocalAllocatedLocation(location);
                    }
                  }
                }
                setShowAllocateVehicle(false);
              }}
              onCancel={onCancel}

            ></VehicleAllocationPickerDialog>
          )}


        </div>

      </Grid>
      {(keyLocation != null || vehicleLocation != null) && (
        <div style={{
          position: "absolute",
          right: 50,
          bottom: 20,
          color: "green",
        }}>
          <b>{t("booking.labels.confirmMovement")}</b>
        </div>
      )}
    </Fragment>
  );
}

export default BookingMovement;
